import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendEmailVerification,
  User,
  deleteUser,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from 'firebase/auth';
import { auth } from '../firebase/config';
import { UserProfile, Language } from '../types/user';
import { createUserProfile, getUserProfile, updateUserProfile, deleteUserProfile } from '../firebase/userService';

export interface AuthContextType {
  currentUser: User | null;
  userProfile: UserProfile | null;
  isAdmin: boolean;
  signin: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string) => Promise<User>;
  signout: () => Promise<void>;
  deleteAccount: (password: string) => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [authUnsubscribe, setAuthUnsubscribe] = useState<(() => void) | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        const profile = await getUserProfile(user.uid);
        if (profile) {
          setUserProfile(profile);
          // Mise à jour de la dernière connexion
          await updateUserProfile(user.uid, { lastLoginDate: new Date().toISOString() });
        } else if (user.emailVerified) {
          // Première connexion après vérification de l'email
          // Récupérer la langue du localStorage
          const storedLanguage = localStorage.getItem('selectedLanguage') as Language || 'en';
          const storedAge = localStorage.getItem('tempAge');
          const storedCountry = localStorage.getItem('tempCountry');
          const storedCity = localStorage.getItem('tempCity');
          // Créer le profil avec la langue stockée
          const newProfile = await createUserProfile(
            user.uid,
            user.email!,
            storedLanguage,
            storedAge ? parseInt(storedAge, 10) : undefined,
            storedCountry || undefined,
            storedCity || undefined
          );
          localStorage.removeItem('tempAge');
          localStorage.removeItem('tempCountry');
          localStorage.removeItem('tempCity');
          setUserProfile(newProfile);
        }
      } else {
        setUserProfile(null);
      }
      setLoading(false);
    });

    setAuthUnsubscribe(() => unsubscribe);

    return unsubscribe;
  }, []);

  const signin = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
  };

  const signup = async (email: string, password: string) => {
    const { user } = await createUserWithEmailAndPassword(auth, email, password);
    // Envoyer l'email de vérification
    await sendEmailVerification(user);
    // Créer le profil utilisateur seulement après la vérification
    // Le profil sera créé lors de la première connexion après vérification
    return user;
  };

  const signout = () => signOut(auth);

  const deleteAccount = async (password: string) => {
    if (!currentUser || !currentUser.email) {
      throw new Error('Aucun utilisateur connecté');
    }
    
    try {
      console.log("Début du processus de suppression de compte");
      
      // Réauthentifier l'utilisateur avant de supprimer le compte
      console.log("Réauthentification de l'utilisateur...");
      const credential = EmailAuthProvider.credential(
        currentUser.email,
        password
      );
      
      await reauthenticateWithCredential(currentUser, credential);
      console.log("Réauthentification réussie");
      
      // ÉTAPE 1 : Annuler l'abonnement Stripe si l'utilisateur en a un
      if (userProfile?.subscriptionId) {
        console.log("Annulation de l'abonnement Stripe...");
        try {
          const response = await fetch(`${import.meta.env.VITE_API_URL}/cancel-subscription`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              userId: userProfile.uid
            }),
          });
          
          if (!response.ok) {
            const errorText = await response.text();
            
            // Exception pour "No such subscription" - on peut continuer car l'abonnement n'existe pas
            if (errorText.includes('No such subscription')) {
              console.log("L'abonnement n'existe pas sur Stripe, on peut continuer");
            } else {
              // Pour toute autre erreur, on arrête le processus
              console.error("Échec de l'annulation de l'abonnement Stripe:", errorText);
              throw new Error(`Impossible d'annuler l'abonnement Stripe. Veuillez réessayer plus tard.`);
            }
          } else {
            console.log("Abonnement Stripe annulé avec succès");
          }
        } catch (stripeError) {
          console.error("Erreur lors de l'annulation de l'abonnement Stripe:", stripeError);
          throw new Error(`Erreur lors de l'annulation de l'abonnement. Veuillez réessayer plus tard.`);
        }
      }
      
      // ÉTAPE 2 : Continuer avec la suppression du compte
      // Désactiver le listener onAuthStateChanged pour éviter la recréation du profil
      if (authUnsubscribe) {
        console.log("Désactivation du listener d'authentification...");
        authUnsubscribe();
      }
      
      // Supprimer les données de l'utilisateur dans Firestore
      if (userProfile) {
        console.log(`Suppression des données utilisateur pour ${userProfile.uid}...`);
        try {
          await deleteUserProfile(userProfile.uid);
          console.log("Données utilisateur supprimées avec succès");
        } catch (error) {
          console.error("Erreur lors de la suppression des données utilisateur:", error);
          throw error; // Arrêter le processus si la suppression des données échoue
        }
      }
      
      // Supprimer le compte Firebase Auth
      console.log("Suppression du compte Firebase Authentication...");
      await deleteUser(currentUser);
      console.log("Compte Firebase Authentication supprimé avec succès");
      
      // Mettre à jour l'état local
      setCurrentUser(null);
      setUserProfile(null);
      
      // La déconnexion sera automatique après la suppression du compte
    } catch (error) {
      console.error("Erreur lors de la suppression du compte:", error);
      
      // Réactiver le listener si une erreur se produit
      if (!authUnsubscribe) {
        const newUnsubscribe = onAuthStateChanged(auth, async (user) => {
          setCurrentUser(user);
          if (user) {
            const profile = await getUserProfile(user.uid);
            setUserProfile(profile || null);
          } else {
            setUserProfile(null);
          }
        });
        setAuthUnsubscribe(() => newUnsubscribe);
      }
      
      // Vérifier le type d'erreur spécifique
      if (error instanceof Error) {
        if (error.message.includes('auth/requires-recent-login')) {
          console.error("La session a expiré, réauthentification nécessaire");
          throw new Error('Votre session a expiré. Veuillez vous reconnecter et réessayer.');
        } else if (error.message.includes('auth/wrong-password')) {
          console.error("Mot de passe incorrect");
          throw new Error('Mot de passe incorrect');
        } else {
          console.error("Autre erreur d'authentification:", error.message);
          throw error;
        }
      } else {
        throw error;
      }
    }
  };

  const value = {
    currentUser,
    userProfile,
    isAdmin: userProfile?.isAdmin || false,
    signin,
    signup,
    signout,
    deleteAccount,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
